import React, { useEffect, useState, useCallback } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from "./config/firebase";
import FooterNav from './components/FooterNav'
import PageHeader from './components/PageHeader'
import LoadingScreen from './components/LoadingScreen'
import moment from 'moment';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import DeleteReservationModal from './components/DeleteReservationModal';
import { doc, getDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';

export default function MyReservations() {
    const [user] = useAuthState(auth);
    const [myGroupCourses, setMyGroupCourses] = useState([]);
    const [userData, setUserData] = useState(null);
    const [myReservations, setMyReservations] = useState(null);
    const [showDeleteReservationModal, setShowDeleteReservationModal] = useState(false);
    const [reservationToDelete, setReservationToDelete] = useState(null);
    const [hasUpcomingReservations, setHasUpcomingReservations] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    //get the users courses
    const refreshUserData = useCallback(async () => {
        if (user?.uid) {
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                setUserData(userDoc.data());
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        }
    }, [user?.uid]);

    // Get users data when user is refreshed and pull courses from it
    useEffect(() => {
        refreshUserData();
    }, [refreshUserData]);

    // When usersData is refreshed, create an array of users courses
    useEffect(() => {
        if (userData?.courses && userData.courses.length > 0) {
            const pushData = [];
            userData.courses.forEach((course) => {
                try {
                    pushData.push({ course: course.course, group: course.group });
                } catch (err) {
                    console.error("Error: ", err);
                }
            });
            setMyGroupCourses(pushData);
        }
    }, [userData]);

    const getMyReservations = useCallback(async () => {
        if (!myGroupCourses?.length || !user?.uid) {
            return;
        }

        const newReservations = [];

        for (const groupCourse of myGroupCourses) {
            try {
                const courseDoc = await getDoc(doc(db, 'groups', groupCourse.group, 'courses', groupCourse.course));

                if (courseDoc.exists()) {
                    const groupCourseName = courseDoc.data().name;
                    const teeTimeGroupsRef = collection(db, 'groups', groupCourse.group, 'courses', groupCourse.course, 'teetimegroups');
                    const q = query(teeTimeGroupsRef, where('uid', '==', user.uid));
                    const querySnapshot = await getDocs(q);

                    querySnapshot.forEach((doc) => {
                        if (doc.data().status === 'confirmed') {
                            newReservations.push({
                                startingTeeID: doc.data().startingTeeID,
                                date: doc.data().date,
                                time: doc.data().startingTime,
                                numPlayers: doc.data().golfers?.length,
                                numHoles: doc.data().numHoles,
                                courseName: groupCourseName,
                                id: doc.id,
                                course: groupCourse.course,
                                group: groupCourse.group,
                                receipt: doc.data().receipt,
                                receiptNumber: doc.data().receiptNumber
                            });
                        }
                    });
                }
            } catch (error) {
                console.error("Error getting document:", error);
            }
        }

        setMyReservations(newReservations);
    }, [myGroupCourses, user?.uid]);

    // Fetch reservations when group courses change
    useEffect(() => {
        getMyReservations();
    }, [getMyReservations]);

    // Check for upcoming reservations
    useEffect(() => {
        if (!myReservations) {
            setHasUpcomingReservations(false);
            return;
        }

        if (myReservations.length === 0) {
            setHasUpcomingReservations(false);
        } else {
            const hasUpcoming = myReservations.some(reservation =>
                moment(reservation.date + ' ' + reservation.time, 'YYYY-MM-DD h:mm a') >= moment().subtract(10, 'minutes')
            );
            setHasUpcomingReservations(hasUpcoming);
        }
    }, [myReservations]);

    const deleteReservation = useCallback((group, course, id) => {
        if (course && group && id) {
            setReservationToDelete({ group: group, course: course, id: id });
            setShowDeleteReservationModal(true);
        } else {
            console.log("We couldn't set the reservation to delete because we didn't have enough info.");
        }
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (reservationToDelete) {
            try {
                await deleteDoc(doc(db, 'groups', reservationToDelete.group, 'courses', reservationToDelete.course, 'teetimegroups', reservationToDelete.id));
                setShowDeleteReservationModal(false);
                getMyReservations();
            } catch (error) {
                console.error("Error deleting reservation: ", error);
            }
        }
    }, [reservationToDelete, getMyReservations]);

    // Set loading to false only when we have all necessary data
    useEffect(() => {
        if (user && userData && myGroupCourses && myReservations !== null) {
            setIsLoading(false);
        }
    }, [user, userData, myGroupCourses, myReservations]);

    useEffect(() => {
        if (myReservations !== null) {
            console.log("My Reservations: ", myReservations);
        }
    }, [myReservations]);

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <div>
            <PageHeader pageTitle='My Reservations' />
            <div className="px-3 text-center">
                {myReservations !== null && !hasUpcomingReservations &&
                    <p>You have no upcoming reservations.</p>
                }
                {myReservations !== null && hasUpcomingReservations &&
                    <p>Here are your upcoming reservations.</p>
                }
                {myReservations?.map((reservation, i) => {
                    if (moment(reservation.date + ' ' + reservation.time, 'YYYY-MM-DD h:mm a') >= moment().subtract(10, 'minutes')) {
                        return (
                            <Card className="mb-3" key={i}>
                                <Card.Body>
                                    <Card.Title className="d-block text-center">
                                        {reservation.courseName}
                                        <br></br><small style={{ fontSize: '60%', opacity: '0.5' }}>ID: {reservation.id}</small>
                                    </Card.Title>
                                    <p className="mb-1">{moment(reservation.date, 'YYYY-MM-DD').format('MMMM Do')} @ {reservation.time}</p>
                                    <p className="mb-1">{reservation.numPlayers > 1 ? reservation.numPlayers + " players" : reservation.numPlayers + " player"} for {reservation.numHoles} holes</p>
                                    {reservation.receipt && reservation.receiptNumber && (
                                        <div className="mb-2" style={{ color: '#198754', fontWeight: '500' }}>Receipt #: {reservation.receipt?.payment?.receiptNumber || reservation.receipt?.payment?.payment?.receiptNumber}</div>
                                    )}
                                </Card.Body>
                                <Card.Footer className="text-center">
                                    <div className="cardFooterButtons d-flex justify-content-center">
                                        {reservation.receipt ?
                                            <div className="text-center text-danger">Call the clubhouse to cancel this reservation.</div>
                                            : <Button disabled={reservation.receipt ? true : false} variant="outline-danger d-block w-100" onClick={() => deleteReservation(reservation.group, reservation.course, reservation.id)} className="border-0">
                                                <FontAwesomeIcon className="text-danger" icon={faTrash} /> Cancel Reservation
                                            </Button>
                                        }
                                    </div>
                                </Card.Footer>
                            </Card>
                        );
                    }
                    return null;
                })}
            </div>
            <DeleteReservationModal
                show={showDeleteReservationModal}
                onHide={() => setShowDeleteReservationModal(false)}
                onConfirm={handleDeleteConfirm}
                reservation={reservationToDelete ? {
                    id: reservationToDelete.id,
                    userId: user?.uid || '',
                    date: '',
                    time: '',
                    courseName: reservationToDelete.course,
                    players: 0,
                    status: 'confirmed',
                    startingTeeID: '',
                    numHoles: 0,
                    course: reservationToDelete.course,
                    group: reservationToDelete.group
                } : null}
            />
            <FooterNav />
        </div>
    )
}
